import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BookOpen, MessageCircle, Mail, CheckCheck, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { cn, formatRelativeTime } from '../lib/utils';
import { inAppNotificationsAPI } from '../lib/api';

const ICON_MAP = {
    new_lesson: BookOpen,
    chat_message: MessageCircle,
    private_message: Mail,
};

const COLOR_MAP = {
    new_lesson: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    chat_message: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    private_message: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
};

export function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const pollRef = useRef(null);

    const fetchUnreadCount = useCallback(async () => {
        try {
            const res = await inAppNotificationsAPI.getUnreadCount();
            setUnreadCount(res.data.count);
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error('Failed to fetch unread count:', err);
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const res = await inAppNotificationsAPI.getAll(30, 0);
            setNotifications(res.data);
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error('Failed to fetch notifications:', err);
        }
        setLoading(false);
    }, []);

    // Poll unread count every 30s
    useEffect(() => {
        fetchUnreadCount();
        pollRef.current = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(pollRef.current);
    }, [fetchUnreadCount]);

    // Fetch full list when dropdown opens
    useEffect(() => {
        if (open) fetchNotifications();
    }, [open, fetchNotifications]);

    // Close on outside click
    useEffect(() => {
        function handleClick(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const handleMarkAllRead = async () => {
        try {
            await inAppNotificationsAPI.markAllRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error('Failed to mark all read:', err);
        }
    };

    const handleClick = async (notif) => {
        if (!notif.is_read) {
            try {
                await inAppNotificationsAPI.markRead(notif.id);
                setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
                setUnreadCount(prev => Math.max(0, prev - 1));
            } catch (err) {
                if (process.env.NODE_ENV === 'development') console.error('Failed to mark read:', err);
            }
        }
        setOpen(false);
        if (notif.link) navigate(notif.link);
    };

    const handleDelete = async (e, notifId) => {
        e.stopPropagation();
        try {
            await inAppNotificationsAPI.delete(notifId);
            const removed = notifications.find(n => n.id === notifId);
            setNotifications(prev => prev.filter(n => n.id !== notifId));
            if (removed && !removed.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error('Failed to delete notification:', err);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <Button
                variant="ghost"
                size="icon"
                className="rounded-full relative"
                onClick={() => setOpen(prev => !prev)}
                aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
                data-testid="notification-bell"
            >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-in zoom-in-50"
                        data-testid="notification-badge"
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </Button>

            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-[360px] max-h-[480px] bg-white dark:bg-gray-900 border border-border rounded-xl shadow-xl z-50 flex flex-col animate-in fade-in-0 slide-in-from-top-2 duration-200"
                    data-testid="notification-dropdown"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                        <h3 className="font-semibold text-sm">Notifications</h3>
                        <div className="flex items-center gap-1">
                            {unreadCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs h-7 px-2"
                                    onClick={handleMarkAllRead}
                                    data-testid="mark-all-read-btn"
                                >
                                    <CheckCheck className="w-3 h-3 mr-1" />
                                    Mark all read
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => setOpen(false)}
                            >
                                <X className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>

                    {/* Notification List */}
                    <div className="overflow-y-auto flex-1">
                        {loading && notifications.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground text-sm">
                                Loading...
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="p-8 text-center" data-testid="notification-empty">
                                <Bell className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                                <p className="text-sm text-muted-foreground">No notifications yet</p>
                                <p className="text-xs text-muted-foreground/70 mt-1">
                                    You&apos;ll be notified about new lessons, chats, and messages
                                </p>
                            </div>
                        ) : (
                            notifications.map(notif => {
                                const Icon = ICON_MAP[notif.type] || Bell;
                                const colorClass = COLOR_MAP[notif.type] || 'bg-muted text-muted-foreground';
                                return (
                                    <div
                                        key={notif.id}
                                        className={cn(
                                            "group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-border/50 last:border-0",
                                            "hover:bg-muted/50",
                                            !notif.is_read && "bg-primary/5"
                                        )}
                                        onClick={() => handleClick(notif)}
                                        data-testid={`notification-item-${notif.id}`}
                                    >
                                        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5", colorClass)}>
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={cn("text-sm leading-snug", !notif.is_read && "font-semibold")}>
                                                    {notif.title}
                                                </p>
                                                {!notif.is_read && (
                                                    <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                                {notif.body}
                                            </p>
                                            <p className="text-[10px] text-muted-foreground/60 mt-1">
                                                {formatRelativeTime(notif.created_at)}
                                            </p>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 flex-shrink-0 opacity-0 group-hover:opacity-100 hover:opacity-100 text-muted-foreground hover:text-destructive"
                                            onClick={(e) => handleDelete(e, notif.id)}
                                            data-testid={`delete-notification-${notif.id}`}
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </Button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
